/**
 * Step 3 of the booking wizard — contact details, deposit summary, T&Cs,
 * and the "Pay £50 deposit" CTA. Receives the entire booking-flow context
 * via a single `ctx` prop to avoid 25+ individual props.
 */
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { ArrowLeft, Check, Loader2 } from "lucide-react";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";

const inputCls = "bg-[#0d0d0d] border-white/10 text-white placeholder:text-zinc-600 focus-visible:ring-[#D946EF] focus-visible:border-[#D946EF] h-12 rounded-sm";

const slotToBlockRange = (slot) => {
  if (!slot) return "";
  if (["09:00", "10:00", "11:00"].includes(slot)) return "9am – 12pm";
  if (["12:00", "13:00", "14:00"].includes(slot)) return "12pm – 3pm";
  if (["15:00", "16:00", "17:00"].includes(slot)) return "3pm – 6pm";
  return slot;
};

export default function StepContact({ ctx }) {
  const {
    name, setName,
    email, setEmail,
    phone, setPhone,
    notes, setNotes,
    agreed, setAgreed,
    service, vehicle, bookingDate, timeSlot,
    selectedPkg, selectedStarlight, selectedAmbient,
    comboSubtotal, comboDiscountPct, comboTotal,
    ssActive, scActive, ssPrice, scPrice,
    promoActive, promoPct, addonTotal, applyPromo,
    submitting, canSubmit, submit, setStep,
    promoCode, setPromoCode, promoState, validatePromo, clearPromo,
    applyCustomerPromo, promoActiveApplied,
  } = ctx;

  // Final totals with the customer-entered promo layered on top of everything else.
  const finalSingle = selectedPkg ? applyCustomerPromo(applyPromo(selectedPkg.price + addonTotal)) : 0;
  const finalCombo = applyCustomerPromo(comboTotal);

  return (
    <div className="space-y-5" data-testid="step-3">
      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <Label className="text-sm text-zinc-300 mb-2 block" htmlFor="name">Full name</Label>
          <Input id="name" data-testid="name-input" value={name} onChange={(e) => setName(e.target.value)} className={inputCls} placeholder="Alex Rivera" />
        </div>
        <div>
          <Label className="text-sm text-zinc-300 mb-2 block" htmlFor="email">Email</Label>
          <Input id="email" type="email" data-testid="email-input" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} placeholder="you@email.com" />
        </div>
        <div className="sm:col-span-2">
          <Label className="text-sm text-zinc-300 mb-2 block" htmlFor="phone">Phone</Label>
          <Input id="phone" data-testid="phone-input" value={phone} onChange={(e) => setPhone(e.target.value)} className={inputCls} placeholder="+44 7…" />
        </div>
        <div className="sm:col-span-2">
          <Label className="text-sm text-zinc-300 mb-2 block" htmlFor="notes">Notes (optional)</Label>
          <Textarea id="notes" data-testid="notes-input" value={notes} onChange={(e) => setNotes(e.target.value)} className={`${inputCls} min-h-32 py-3`} placeholder="Tell us about colours, patterns, references…" />
        </div>

        {/* Promo code */}
        <div className="sm:col-span-2">
          <Label className="text-sm text-zinc-300 mb-2 block" htmlFor="promo-code">Promo code (optional)</Label>
          <div className="flex gap-2">
            <Input
              id="promo-code"
              data-testid="promo-code-input"
              value={promoCode}
              onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
              disabled={promoActiveApplied}
              className={`${inputCls} flex-1 uppercase tracking-wider`}
              placeholder="e.g. LACHQ10"
            />
            {promoActiveApplied ? (
              <button
                type="button"
                onClick={clearPromo}
                data-testid="promo-clear-btn"
                className="px-5 h-12 rounded-sm border border-white/15 text-zinc-300 hover:border-white/40 hover:text-white transition text-sm"
              >
                Remove
              </button>
            ) : (
              <button
                type="button"
                onClick={validatePromo}
                disabled={!promoCode.trim() || promoState.status === "checking"}
                data-testid="promo-apply-btn"
                className="px-5 h-12 rounded-sm bg-[#D946EF] text-white font-semibold disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#F0ABFC] transition text-sm"
              >
                {promoState.status === "checking" ? "Checking…" : "Apply"}
              </button>
            )}
          </div>
          {promoActiveApplied && (
            <p data-testid="promo-applied-note" className="mt-2 text-xs text-[#34d399]">
              ✓ {promoCode} applied — {promoState.discount_pct}% off {promoState.label ? `· ${promoState.label}` : ""}
            </p>
          )}
          {promoState.status === "invalid" && promoState.error && (
            <p data-testid="promo-error-note" className="mt-2 text-xs text-red-400">{promoState.error}</p>
          )}
        </div>
      </div>

      <div className="p-5 rounded-sm border border-white/10 bg-[#0a0a0a] text-sm">
        <div className="text-xs tracking-[0.3em] text-[#D946EF] mb-3">SUMMARY</div>
        <div className="grid grid-cols-2 gap-y-2 text-zinc-300">
          <div className="text-zinc-500">Service</div><div>{service}</div>
          {service === "Combo" && selectedStarlight && selectedAmbient && (
            <>
              <div className="text-zinc-500">Starlight</div><div>{selectedStarlight.name} · £{selectedStarlight.price.toFixed(0)}</div>
              <div className="text-zinc-500">Ambient</div><div>{selectedAmbient.name} · £{selectedAmbient.price.toFixed(0)}</div>
              <div className="text-zinc-500">Subtotal</div><div>£{comboSubtotal.toFixed(0)}</div>
              <div className="text-[#34d399]">Combo discount</div><div className="text-[#34d399]">-{comboDiscountPct}%</div>
              {promoActiveApplied && (<><div className="text-[#34d399]">{promoCode} ({promoState.discount_pct}% off)</div><div className="text-right text-[#34d399]">-£{Math.round(comboTotal * promoState.discount_pct / 100)}</div></>)}
              <div className="text-white font-semibold">Total</div><div className="font-display font-bold text-[#D946EF]">£{finalCombo.toFixed(0)}</div>
            </>
          )}
          {service !== "Combo" && selectedPkg && (
            <>
              <div className="text-zinc-500">Package</div><div>{selectedPkg.name} · £{selectedPkg.price.toFixed(0)}</div>
              {ssActive && (<><div className="text-zinc-500">Shooting Stars</div><div>+£{ssPrice}</div></>)}
              {scActive && (<><div className="text-zinc-500">Screen Code</div><div>+£{scPrice}</div></>)}
              {promoActive && (<><div className="text-[#34d399]">Eid {promoPct}% off</div><div className="text-right text-[#34d399]">-£{Math.round((selectedPkg.price + addonTotal) * promoPct / 100)}</div></>)}
              {promoActiveApplied && (<><div className="text-[#34d399]">{promoCode} ({promoState.discount_pct}% off)</div><div className="text-right text-[#34d399]">-£{Math.round(applyPromo(selectedPkg.price + addonTotal) * promoState.discount_pct / 100)}</div></>)}
              <div className="text-white font-semibold">Total</div><div className="font-display font-bold text-[#D946EF]">£{finalSingle.toFixed(0)}</div>
            </>
          )}
          <div className="text-zinc-500">Vehicle</div><div>{vehicle}</div>
          <div className="text-zinc-500">Date</div><div>{bookingDate && format(bookingDate, "EEEE d MMM yyyy")}</div>
          <div className="text-zinc-500">Time</div><div>{slotToBlockRange(timeSlot)}</div>
        </div>
        <div className="mt-4 pt-4 border-t border-white/10 flex items-center justify-between">
          <div>
            <div className="text-xs text-zinc-500">Deposit to secure this slot</div>
            <div className="font-display font-bold text-2xl text-white mt-0.5">£50.00</div>
          </div>
          <div className="text-right">
            {service === "Combo" && selectedStarlight && selectedAmbient ? (
              <>
                <div className="text-[10px] tracking-[0.2em] text-zinc-500">BALANCE ON THE DAY</div>
                <div className="text-xl font-display font-bold text-[#D946EF]">£{Math.max(0, finalCombo - 50).toFixed(0)}</div>
              </>
            ) : selectedPkg ? (
              <>
                <div className="text-[10px] tracking-[0.2em] text-zinc-500">BALANCE ON THE DAY</div>
                <div className="text-xl font-display font-bold text-[#D946EF]">£{Math.max(0, finalSingle - 50).toFixed(0)}</div>
              </>
            ) : (
              <>
                <div className="text-[10px] tracking-[0.2em] text-zinc-500">REFUNDED</div>
                <div className="text-xs text-zinc-300">on the day, off your final invoice</div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Terms & Conditions checkbox — required before deposit */}
      <label
        htmlFor="agree-tc"
        data-testid="agree-tc-row"
        className={`flex items-start gap-3 p-4 rounded-sm border cursor-pointer transition select-none ${
          agreed ? "border-[#D946EF]/60 bg-[#D946EF]/5" : "border-white/10 bg-[#0a0a0a] hover:border-white/30"
        }`}
      >
        <input
          id="agree-tc"
          type="checkbox"
          checked={agreed}
          onChange={(e) => setAgreed(e.target.checked)}
          data-testid="agree-tc-checkbox"
          className="mt-1 w-4 h-4 rounded-sm accent-[#D946EF] cursor-pointer shrink-0"
        />
        <div className="text-sm text-zinc-300 leading-relaxed">
          I agree to LAC's{" "}
          <Link to="/terms" target="_blank" rel="noopener noreferrer" data-testid="agree-tc-link" className="text-[#D946EF] hover:text-[#F0ABFC] underline underline-offset-2">
            Terms &amp; Conditions
          </Link>
          . I understand the <b className="text-white">£50 deposit is non-refundable</b>{" "}
          unless LAC is unable to complete my booking, in which case I'll be refunded in full.
        </div>
      </label>

      <div className="flex justify-between">
        <button onClick={() => setStep(2)} className="inline-flex items-center gap-2 text-zinc-400 hover:text-white transition" data-testid="back-btn-3">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <button
          disabled={!canSubmit || submitting}
          onClick={submit}
          data-testid="submit-booking-btn"
          className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-[#D946EF] text-white font-semibold disabled:opacity-30 disabled:cursor-not-allowed hover:bg-[#F0ABFC] transition"
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
          Pay £50 deposit & confirm
        </button>
      </div>
    </div>
  );
}
